import { ATSPrincipalAppCandidatesListTestIds } from 'data-testids/ATS';
import { Link, Stack, TableRow, Typography, useTheme } from '@mui/material';
import {
  CandidateActionStatus,
  CandidateDetailsCellProps,
  CandidateRowProps,
  DetailsCellProps,
  PrincipalCandidatesListRole,
} from 'features/Principal/types/candidatesListTypes';
import { getStatusText } from 'components/CreateJobOrTemplateModal/utils';
import { StyledTableCell } from '../styles';
import CircleIcon from '@mui/icons-material/Circle';
import { useJobStatusColor } from 'utils/statusUtils';
import { CandidateActionButtonsContainer } from './CandidateActionButtonsContainer';
import { formatPhoneNumber } from 'utils/util';
import {
  HighlyRelevantSkillsBadge,
  HighRetentionPossibleBadge,
  CredentialVerifiedBadge,
} from 'sharedComponents/Badge/CandidateBadges';
import { CandidateCredentialStatus } from 'utils/typedEnums';
import { TooltipIconButton } from 'sharedComponents/Buttons/TooltipIconButton';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';

export const DetailsCell: React.FC<DetailsCellProps> = ({ hasReferences, onReferenceClick }) => {
  const theme = useTheme();

  return (
    <StyledTableCell scope="row" sx={{ width: '20%' }}>
      <>
        {false && (
          <TooltipIconButton
            dataTestId={ATSPrincipalAppCandidatesListTestIds.SCORECARD_ICON_BUTTON}
            tooltipTitle="Add a scorecard"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <StarBorderIcon />
          </TooltipIconButton>
        )}
        <TooltipIconButton
          dataTestId={ATSPrincipalAppCandidatesListTestIds.REFERENCE_ICON_BUTTON}
          tooltipTitle={hasReferences ? 'Completed reference check' : 'Add reference check'}
          onClick={(event) => {
            event.stopPropagation();
            onReferenceClick();
          }}
        >
          <PersonOutlinedIcon
            sx={{ color: hasReferences ? theme.palette.success.main : 'inherit' }}
          />
        </TooltipIconButton>
        <TooltipIconButton
          dataTestId={ATSPrincipalAppCandidatesListTestIds.APPLICATION_ICON_BUTTON}
          tooltipTitle="View application"
        >
          <AttachFileOutlinedIcon sx={{ color: theme.palette.primary.main }} />
        </TooltipIconButton>
      </>
    </StyledTableCell>
  );
};

export const CandidateCell: React.FC<CandidateDetailsCellProps> = ({
  candidate,
  isHighRetention,
  isTopCandidate,
  isVerified,
}) => {
  const theme = useTheme();

  return (
    <StyledTableCell scope="row" align="left" sx={{ width: '27%' }}>
      <Stack direction="row" alignItems="center" sx={{ pb: theme.spacing(1) }}>
        <Typography variant="h3" data-testid={ATSPrincipalAppCandidatesListTestIds.CANDIDATE_NAME}>
          {candidate.first_name} {candidate.last_name} &nbsp;
        </Typography>
        {isTopCandidate && (
          <HighlyRelevantSkillsBadge
            dataTestId={ATSPrincipalAppCandidatesListTestIds.HIGHLY_RELEVANT_SKILLS_BADGE}
            showTooltip
          />
        )}
        {isHighRetention && (
          <HighRetentionPossibleBadge
            dataTestId={ATSPrincipalAppCandidatesListTestIds.HIGH_RETENTION_BADGE}
            showTooltip
          />
        )}
        {isVerified && (
          <CredentialVerifiedBadge
            dataTestId={ATSPrincipalAppCandidatesListTestIds.VERIFIED_BADGE}
            showTooltip
          />
        )}
      </Stack>
      <Typography
        data-testid={ATSPrincipalAppCandidatesListTestIds.CANDIDATE_EMAIL}
        sx={{ pb: theme.spacing(1) }}
      >
        Email: {candidate.email}
      </Typography>
      {candidate.profile.phone_cell && (
        <Typography data-testid={ATSPrincipalAppCandidatesListTestIds.CANDIDATE_CELL_PHONE}>
          Phone: {formatPhoneNumber(candidate.profile.phone_cell)} (cell)
        </Typography>
      )}
      {candidate.profile.phone_work && (
        <Typography data-testid={ATSPrincipalAppCandidatesListTestIds.CANDIDATE_WORK_PHONE}>
          {formatPhoneNumber(candidate.profile.phone_work)} (work)
        </Typography>
      )}
      {candidate.profile.phone_home && (
        <Typography data-testid={ATSPrincipalAppCandidatesListTestIds.CANDIDATE_HOME_PHONE}>
          {formatPhoneNumber(candidate.profile.phone_home)} (home)
        </Typography>
      )}
    </StyledTableCell>
  );
};

export const CandidateActionsCell: React.FC<{
  applicationId: number;
  principalStage?: CandidateActionStatus;
}> = ({ applicationId, principalStage }) => {
  const theme = useTheme();

  return (
    <StyledTableCell scope="row" sx={{ paddingLeft: theme.spacing(4) }}>
      <CandidateActionButtonsContainer
        applicationId={applicationId}
        initialStatus={principalStage}
        variant="list"
      />
    </StyledTableCell>
  );
};

export const JobDetailsCell: React.FC<{ role: PrincipalCandidatesListRole }> = ({ role }) => {
  const theme = useTheme();
  const styling = useJobStatusColor(role.active_status, role.status);
  const statusText = getStatusText(role.status, role.active_status);

  const handleLinkClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <StyledTableCell scope="row" sx={{ width: '25%' }}>
      <Link
        href={`/jobview/${role.id}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleLinkClick}
        underline="hover"
        data-testid={ATSPrincipalAppCandidatesListTestIds.JOB_TITLE}
        sx={{
          color: theme.palette.text.primary,
          fontWeight: theme.typography.h3.fontWeight,
          fontSize: theme.typography.h3.fontSize,
          display: 'block',
          pb: theme.spacing(1),
        }}
      >
        {role.title}
      </Link>
      <Stack direction="row" alignItems="center">
        <CircleIcon
          sx={{
            color: styling.textColor,
            fontSize: '8px',
            marginRight: theme.spacing(0.5),
          }}
        />
        <Typography
          data-testid={ATSPrincipalAppCandidatesListTestIds.JOB_STATUS}
          sx={{ color: styling.textColor }}
        >
          {statusText}
        </Typography>
      </Stack>
    </StyledTableCell>
  );
};

export const CandidateRow: React.FC<CandidateRowProps> = ({
  index,
  onClick,
  onReferenceClick,
  row,
}) => {
  const theme = useTheme();
  const isVerified =
    row.candidate.candidate_credential_status === CandidateCredentialStatus.ALL_VERIFIED ||
    row.candidate.candidate_credential_status === CandidateCredentialStatus.SOME_VERIFIED;

  return (
    <TableRow
      onClick={onClick}
      data-testid={
        index === 0 ? ATSPrincipalAppCandidatesListTestIds.FIRST_CANDIDATE_ROW : undefined
      }
      sx={{
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.gray.lightest,
          transition: '0.3s',
        },
      }}
    >
      <CandidateCell
        candidate={row.candidate}
        isHighRetention={row.application.high_retention}
        isTopCandidate={row.application.top_candidate}
        isVerified={isVerified}
      />
      <JobDetailsCell role={row.role} />
      <DetailsCell hasReferences={row.has_references} onReferenceClick={onReferenceClick} />
      {row?.application && (
        <CandidateActionsCell
          applicationId={row.application.id}
          principalStage={row.candidate_stage}
        />
      )}
    </TableRow>
  );
};
